import React, { useEffect, useState } from 'react'
import { Edit, useForm } from '@refinedev/antd'
import { useInvalidate, type HttpError } from '@refinedev/core'
import { Alert, App, Form, Input, Select } from 'antd'
import { fmtDate } from '../reports/reportUiKit'
import { SELECT, type Suggestion } from './list'
import { STATUS_OPTIONS, statusLabel, type SuggestionStatus } from './suggestionStatus'
import { NOTE_MAX, normalizeNote, useSuggestionAdminNote, writeSuggestionAdminNote } from './adminNote'

interface TriageValues {
  status: SuggestionStatus
  note?: string
}

/**
 * Suggestion triage — the status and the private admin note, saved as TWO writes
 * to TWO tables, in this order:
 *
 *   1. `suggestions` gets `{ status }` and nothing else. Never the body: it is
 *      the member's words, the column grant is `update (status)` only, and a
 *      server trigger refuses any other change. The read-only context below has
 *      no Form.Item `name`, so it can never be submitted.
 *   2. `suggestion_admin_notes` gets `{ suggestion_id, note }` as an upsert, or a
 *      delete when the box was cleared (blank = no note row — see adminNote.ts).
 *      Skipped entirely when the note did not change.
 *
 * The two writes cannot share a transaction from the browser, so the one partial
 * outcome — status saved, note NOT saved — is shown in an Alert on the page and
 * the admin stays here with their note still in the box to retry. The typed note
 * is also pinned into the form's initialValues (`noteDraft`), because the status
 * write invalidates the record, Refine's useForm then calls resetFields(), and
 * without the pin that reset would silently put the OLD note back in the box.
 *
 * The note box stays disabled until the saved note has loaded, and if it fails
 * to load no note is written at all — a save must never overwrite a note the
 * admin could not see. No Delete anywhere: suggestions has no admin DELETE policy.
 */
export function SuggestionEdit() {
  const { message } = App.useApp()
  const invalidate = useInvalidate()
  const { formProps, saveButtonProps, query, form, onFinish, redirect, id } = useForm<
    Suggestion,
    HttpError,
    TriageValues
  >({
    queryMeta: { select: SELECT },
    // Redirect by hand, only once BOTH writes landed — a partial failure must
    // keep the admin on this page.
    redirect: false,
    successNotification: false,
  })
  const record = query?.data?.data
  const suggestionId = id !== undefined ? String(id) : undefined
  const noteState = useSuggestionAdminNote(suggestionId)
  const loadedNote = noteState.kind === 'loaded' ? (noteState.note?.note ?? '') : undefined

  const [saving, setSaving] = useState(false)
  const [noteFailure, setNoteFailure] = useState<string | null>(null)
  const [noteDraft, setNoteDraft] = useState<string | null>(null)

  // The saved note arrives separately from the record — drop it into the box
  // once it has loaded (the box is disabled until then, so nothing is overwritten).
  useEffect(() => {
    if (loadedNote !== undefined) form.setFieldValue('note', noteDraft ?? loadedNote)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedNote])

  async function handleFinish(values: TriageValues) {
    if (!suggestionId) return
    setSaving(true)
    setNoteFailure(null)
    try {
      try {
        await onFinish({ status: values.status })
      } catch {
        // Refine's error notification already said why; nothing was written.
        return
      }

      let noteOutcome: 'saved' | 'removed' | 'unchanged' = 'unchanged'
      if (loadedNote !== undefined && normalizeNote(values.note) !== normalizeNote(loadedNote)) {
        try {
          noteOutcome = await writeSuggestionAdminNote(suggestionId, values.note ?? '')
        } catch (e) {
          const why = e instanceof Error ? e.message : String(e)
          const typed = values.note ?? ''
          setNoteDraft(typed)
          form.setFieldValue('note', typed)
          setNoteFailure(why)
          message.error(`Status saved, but the private note was not saved: ${why}`, 8)
          return
        }
        await invalidate({ resource: 'suggestion_admin_notes', invalidates: ['list'] })
      }

      setNoteDraft(null)
      const noteWords =
        noteOutcome === 'saved' ? ' Private note saved.' : noteOutcome === 'removed' ? ' Private note removed.' : ''
      message.success(`Saved — status is ${statusLabel(values.status)}.${noteWords}`)
      redirect('show', suggestionId)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Edit
      canDelete={false}
      saveButtonProps={{
        ...saveButtonProps,
        loading: saving,
        disabled: saveButtonProps.disabled || saving,
      }}
    >
      <div style={{ maxWidth: 720 }}>
        {noteFailure ? (
          <Alert
            data-testid="suggestion-note-failed"
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Status saved — the private note was NOT saved"
            description={`${noteFailure} Your note is still in the box below; press Save to try again.`}
          />
        ) : null}
        {noteState.kind === 'error' ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="The private note could not be loaded"
            description="It can't be edited right now, so saving will change the status only. Refresh to try again."
          />
        ) : null}

        <Form
          {...formProps}
          initialValues={{ ...(formProps.initialValues ?? {}), note: noteDraft ?? loadedNote ?? '' }}
          onFinish={handleFinish}
          layout="vertical"
        >
          {/* Display-only context — no `name`, so none of it is ever submitted. */}
          <Form.Item label="From">
            <Input
              value={
                record
                  ? `${record.author?.name ?? 'Unknown member'}${record.author?.class_year ? ` · Class of ${record.author.class_year}` : ''}`
                  : '—'
              }
              disabled
            />
          </Form.Item>
          <Form.Item label="Suggestion">
            <Input.TextArea value={record?.body ?? '—'} disabled autoSize={{ minRows: 2, maxRows: 12 }} />
          </Form.Item>
          <Form.Item label="Sent">
            <Input value={fmtDate(record?.created_at)} disabled />
          </Form.Item>

          <Form.Item label="Status" name="status" rules={[{ required: true }]}>
            <Select options={STATUS_OPTIONS} placeholder="Select status" />
          </Form.Item>

          <Form.Item
            label="Private note — admins only"
            name="note"
            extra="Members never see this note. Clear the box and save to remove it."
          >
            <Input.TextArea
              maxLength={NOTE_MAX}
              showCount
              autoSize={{ minRows: 3, maxRows: 12 }}
              disabled={noteState.kind !== 'loaded'}
              placeholder={noteState.kind === 'loading' ? 'Loading the saved note…' : 'Why this status, who owns it, what happens next…'}
            />
          </Form.Item>
        </Form>
      </div>
    </Edit>
  )
}
